import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import oidcConfig from '../services/cognitoConfig';

const AuthContext = createContext(null);

// ─────────────────────────────────────────────────────────────────────────────
// JWT helpers
// ─────────────────────────────────────────────────────────────────────────────
function parseIdToken(idToken) {
  try {
    const payload = idToken.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    const givenName = decoded.given_name || '';
    const familyName = decoded.family_name || '';
    const fullName = [givenName, familyName].filter(Boolean).join(' ') || decoded.name || decoded['cognito:username'] || decoded.sub || '';

    // Extract userId: identities → preferred_username → cognito:username (part after _) → sub
    let userId = '';
    if (decoded.identities && Array.isArray(decoded.identities) && decoded.identities.length > 0) {
      userId = decoded.identities[0].userId || '';
    }
    if (!userId && decoded.preferred_username) {
      userId = decoded.preferred_username;
    }
    if (!userId && decoded['cognito:username']) {
      const cognitoUser = decoded['cognito:username'];
      userId = cognitoUser.includes('_') ? cognitoUser.split('_').slice(1).join('_') : cognitoUser;
    }
    if (!userId) {
      userId = decoded.sub || '';
    }

    // Extract role / group memberships
    const groups = Array.isArray(decoded['cognito:groups']) ? decoded['cognito:groups'] : [];

    const isProductManager = groups.includes('ACCESS_REVIEW_ADMIN');

    return {
      userId,
      name: fullName,
      email: decoded.email || '',
      givenName,
      familyName,
      isProductManager,
    };
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Session persistence
// ─────────────────────────────────────────────────────────────────────────────
function persistSession(userData, accessToken, refreshToken, expiresAt) {
  if (userData) {
    sessionStorage.setItem('ar_user', JSON.stringify(userData));
  } else {
    sessionStorage.removeItem('ar_user');
  }
  if (accessToken) {
    sessionStorage.setItem('ar_token', accessToken);
  } else {
    sessionStorage.removeItem('ar_token');
  }
  if (refreshToken) {
    sessionStorage.setItem('ar_refresh_token', refreshToken);
  } else {
    sessionStorage.removeItem('ar_refresh_token');
  }
  if (expiresAt) {
    sessionStorage.setItem('ar_token_expires_at', String(expiresAt));
  } else {
    sessionStorage.removeItem('ar_token_expires_at');
  }
}

function getStoredSession() {
  try {
    const accessToken = sessionStorage.getItem('ar_token');
    const userStr = sessionStorage.getItem('ar_user');
    const expiresAt = Number(sessionStorage.getItem('ar_token_expires_at') || '0');
    if (accessToken && userStr) {
      const user = JSON.parse(userStr);
      return { user, accessToken, expiresAt };
    }
  } catch { /* ignore */ }
  return null;
}

function clearSession() {
  sessionStorage.removeItem('ar_user');
  sessionStorage.removeItem('ar_token');
  sessionStorage.removeItem('ar_refresh_token');
  sessionStorage.removeItem('ar_token_expires_at');
  sessionStorage.removeItem('oidc_state');
  isRedirecting = false;
}

// ─────────────────────────────────────────────────────────────────────────────
// OIDC helpers
// ─────────────────────────────────────────────────────────────────────────────

function generateState() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

let isRedirecting = false;

// Loop detection: prevent rapid repeated redirects to OIDC provider
const LOGIN_LOOP_THRESHOLD_MS = 10_000; // 10 seconds

function redirectToLogin() {
  if (isRedirecting) return; // prevent multiple redirects

  // Loop detection: if we redirected recently and came back without a code,
  // don't redirect again — user would be stuck in an infinite loop
  const lastAttempt = Number(sessionStorage.getItem('ar_login_attempt') || '0');
  if (lastAttempt && Date.now() - lastAttempt < LOGIN_LOOP_THRESHOLD_MS) {
    console.error('Login redirect loop detected — aborting redirect');
    sessionStorage.removeItem('ar_login_attempt');
    return;
  }

  // Save the current path so we can restore it after login
  const intendedPath = window.location.pathname;
  if (intendedPath && intendedPath !== '/') {
    sessionStorage.setItem('ar_intended_path', intendedPath);
  }

  isRedirecting = true;
  sessionStorage.setItem('ar_login_attempt', String(Date.now()));

  const state = generateState();
  sessionStorage.setItem('oidc_state', state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: oidcConfig.clientId,
    scope: oidcConfig.scope,
    redirect_uri: oidcConfig.redirectUri,
    state,
    identity_provider: oidcConfig.idp,
  });

  window.location.href = `${oidcConfig.authEndpoint}?${params.toString()}`;
}

/** Exchange authorization code for tokens */
async function exchangeCodeForTokens(code) {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: oidcConfig.clientId,
    code,
    redirect_uri: oidcConfig.redirectUri,
  });

  const res = await fetch(oidcConfig.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Token exchange failed: ${errText}`);
  }

  return res.json(); // { id_token, access_token, refresh_token, expires_in, token_type }
}

/** Refresh tokens using refresh_token */
async function refreshTokens(refreshToken) {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: oidcConfig.clientId,
    refresh_token: refreshToken,
  });

  const res = await fetch(oidcConfig.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });

  if (!res.ok) {
    throw new Error('Token refresh failed');
  }

  return res.json(); // { id_token, access_token, expires_in, token_type } — no new refresh_token
}

/** Revoke the refresh token */
async function revokeToken(refreshToken) {
  try {
    await fetch(oidcConfig.revokeEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        token: refreshToken,
        client_id: oidcConfig.clientId,
      }).toString(),
    });
  } catch { /* best effort */ }
}

/** Calculate expiry timestamp from expires_in (seconds) */
function calcExpiresAt(expiresIn) {
  return Date.now() + (expiresIn * 1000);
}

// Refresh 5 minutes before expiry
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const codeExchangeInProgress = useRef(false);

  // ── Schedule silent token refresh ─────────────────────────────────────────
  const scheduleRefresh = useCallback((expiresAt) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const msUntilRefresh = expiresAt - Date.now() - REFRESH_BUFFER_MS;

    if (msUntilRefresh <= 0) {
      // Already expired or about to — refresh immediately
      doRefresh();
      return;
    }

    console.log(`Token refresh scheduled in ${Math.round(msUntilRefresh / 1000)}s`);
    refreshTimerRef.current = setTimeout(doRefresh, msUntilRefresh);
  }, []);

  const doRefresh = useCallback(async () => {
    const storedRefresh = sessionStorage.getItem('ar_refresh_token');
    if (!storedRefresh) {
      console.warn('No refresh token available — redirecting to login');
      clearSession();
      setUser(null);
      setToken(null);
      redirectToLogin();
      return;
    }

    try {
      console.log('Refreshing access token...');
      const tokens = await refreshTokens(storedRefresh);
      const newIdToken = tokens.id_token;
      const expiresAt = calcExpiresAt(tokens.expires_in || 1800);

      // Parse user from new id_token
      const parsed = newIdToken ? parseIdToken(newIdToken) : null;

      // Update state — use id_token for API calls
      setToken(newIdToken);
      if (parsed) setUser(parsed);

      // Persist — keep existing refresh_token (Cognito doesn't return a new one on refresh)
      persistSession(parsed || user, newIdToken, storedRefresh, expiresAt);

      // Schedule next refresh
      scheduleRefresh(expiresAt);

      console.log('Token refreshed successfully');
    } catch (err) {
      console.error('Token refresh failed:', err);
      clearSession();
      setUser(null);
      setToken(null);
      redirectToLogin();
    }
  }, [user, scheduleRefresh]);

  // ── On mount: handle callback code or restore session ─────────────────────
  useEffect(() => {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');

    if (code) {
      // Prevent StrictMode double-run from processing the same code twice
      if (codeExchangeInProgress.current) return;
      codeExchangeInProgress.current = true;

      // ── Callback from OIDC provider ───────────────────────────────────
      const savedState = sessionStorage.getItem('oidc_state');
      if (state && savedState && state !== savedState) {
        console.error('OIDC state mismatch — possible CSRF attack');
        codeExchangeInProgress.current = false;
        clearSession();
        setLoading(false);
        return;
      }

      // Successful callback — clear login attempt tracker and redirecting flag
      sessionStorage.removeItem('oidc_state');
      sessionStorage.removeItem('ar_login_attempt');
      isRedirecting = false;

      // Restore the originally intended path (e.g. /admin-dashboard), falling
      // back to the redirect URI's own pathname if nothing was saved.
      const intendedPath = sessionStorage.getItem('ar_intended_path') || url.pathname;
      sessionStorage.removeItem('ar_intended_path');
      window.history.replaceState({}, document.title, intendedPath);

      exchangeCodeForTokens(code)
        .then((tokens) => {
          const idToken = tokens.id_token;
          const accessToken = tokens.access_token;
          const parsed = parseIdToken(idToken);
          const expiresAt = calcExpiresAt(tokens.expires_in || 1800);

          if (parsed) {
            setUser(parsed);
            setToken(idToken);
            persistSession(parsed, idToken, tokens.refresh_token || '', expiresAt);
            scheduleRefresh(expiresAt);
          }
        })
        .catch((err) => {
          console.error('Token exchange error:', err);
          codeExchangeInProgress.current = false;
        })
        .finally(() => setLoading(false));
    } else {
      // ── Restore existing session ──────────────────────────────────────
      const stored = getStoredSession();
      if (stored) {
        setUser(stored.user);
        setToken(stored.accessToken);

        // Check if token is still valid, schedule refresh
        if (stored.expiresAt) {
          if (Date.now() >= stored.expiresAt) {
            // Token expired — try to refresh immediately
            doRefresh().finally(() => setLoading(false));
            return;
          }
          scheduleRefresh(stored.expiresAt);
        }
        setLoading(false);
      } else if (!codeExchangeInProgress.current) {
        // No session, no auth code, no pending exchange — redirect to OIDC login
        setLoading(false);
        redirectToLogin();
      }
    }

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(() => {
    redirectToLogin();
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    const storedRefresh = sessionStorage.getItem('ar_refresh_token');
    if (storedRefresh) await revokeToken(storedRefresh);

    setUser(null);
    setToken(null);
    clearSession();

    // Chain: PKMS logout (clears SSO session) → Cognito logout (clears Cognito session) → back to app
    const cognitoLogoutParams = new URLSearchParams({
      client_id: oidcConfig.clientId,
      logout_uri: oidcConfig.redirectUri,  // land back on app after Cognito clears session
    });
    const cognitoLogoutUrl = `${oidcConfig.logoutEndpoint}?${cognitoLogoutParams.toString()}`;

    // PKMS clears SSO cookies first, then redirects to Cognito logout
    const pkmsLogoutUrl = `${oidcConfig.pkmsLogout}?appurl=${encodeURIComponent(cognitoLogoutUrl)}`;

    window.location.href = pkmsLogoutUrl;
  }, []);

  if (loading) return null;

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
